// Top-right brain icon that opens a popover bound to useAiMemory.
// Reuses the existing user_ai_memory plumbing wired into every Hub
// edge function and the new ai-route function.
import { useEffect, useState } from "react";
import { Brain, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useAiMemory, AI_MEMORY_MAX_CHARS } from "@/hooks/useAiMemory";
import { useAuth } from "@/hooks/useAuth";

export default function MemoryPopover({ hideTrigger = false }: { hideTrigger?: boolean }) {
  const { user } = useAuth();
  const { instructions, setInstructions, save, loading, saving } = useAiMemory();
  const [open, setOpen] = useState(false);
  const hasMemory = !!instructions.trim();

  // Allow other UI (e.g. the side rail "Brain" item) to open this popover.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-memory", handler);
    return () => window.removeEventListener("open-memory", handler);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {hideTrigger ? (
          <span aria-hidden className="fixed left-1/2 top-16 -translate-x-1/2 block w-0 h-0 pointer-events-none" />
        ) : (
          <button
            type="button"
            aria-label="AI memory"
            title={hasMemory ? "AI is using your memory" : "Set your AI memory"}
            className="relative h-9 w-9 grid place-items-center rounded-full border border-border bg-card/40 hover:border-primary/50 hover:bg-card transition-colors"
          >
            <Brain className="w-4 h-4" />
            {hasMemory && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 border-border">
        <div className="p-4 border-b border-border">
          <h3 className="font-mono font-semibold text-sm flex items-center gap-2">
            <Brain className="w-4 h-4 text-primary" /> Shared AI memory
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Every AI call in HackerLabs reads from this. Your role, tone, ongoing projects, preferences.
          </p>
        </div>
        <div className="p-4">
          {!user ? (
            <p className="text-xs text-muted-foreground">Sign in to save memory.</p>
          ) : loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value.slice(0, AI_MEMORY_MAX_CHARS))}
                rows={8}
                placeholder="I'm a founder building a B2B SaaS. Reply concisely. I prefer TypeScript + Tailwind. Default to dark UI…"
                className="text-xs resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-muted-foreground font-mono">
                  {instructions.length} / {AI_MEMORY_MAX_CHARS}
                </span>
                <button
                  type="button"
                  disabled={saving}
                  onClick={async () => { await save(instructions); setOpen(false); }}
                  className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-mono font-semibold disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save memory"}
                </button>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
